/*
  SOLVÉRA premium product media lightbox.

  Replaces the viewer logic of Dawn's product-modal.js (stock file kept in the
  repo, no longer referenced). Registers the SAME <product-modal> custom
  element, so every existing Dawn contract keeps working unchanged:
    - modal-opener [data-modal="#ProductModal-…"] triggers (global.js)
    - ModalDialog show/hide, ESC, focus trap, body scroll lock
    - product-info.js variant swap of `.product-media-modal__content` innerHTML
      (all listeners here are delegated from the root element for that reason)
    - theme-editor.js hideProductModal()

  Gesture model (PhotoSwipe/Apple-Photos conventions):
    scale == 1: horizontal drag moves the track (swipe between media),
                vertical drag closes, double-tap/click zooms in.
    scale > 1 : drag pans the image with clamped bounds + momentum,
                pinch/wheel zooms anchored at the pointer, double-tap resets.
*/

if (!customElements.get('product-modal')) {
  const MAX_ZOOM = 5;
  const DOUBLE_TAP_ZOOM = 2.5;
  const SWIPE_DISTANCE_RATIO = 0.2; // fraction of viewport width
  const SWIPE_VELOCITY = 0.5; // px/ms
  const CLOSE_DRAG_DISTANCE = 90; // px
  const CLOSE_DRAG_VELOCITY = 0.55; // px/ms
  const DRAG_SLOP = 8; // px before a gesture commits
  const DOUBLE_TAP_MS = 350;
  const DOUBLE_TAP_RADIUS = 40;

  customElements.define(
    'product-modal',
    class ProductLightbox extends ModalDialog {
      constructor() {
        super();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.pointers = new Map();
        this.gesture = null;
        this.lastTap = null;
        this.singleTapTimer = null;
        this.rafId = null;
        this.closing = false;
        this.index = 0;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.trackX = 0;

        this.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('dragstart', (event) => event.preventDefault());
        // Old iOS Safari fires proprietary gesture events that trigger page zoom.
        ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) =>
          this.addEventListener(type, (event) => event.preventDefault())
        );

        this.onViewportChange = this.onViewportChange.bind(this);
      }

      /* ------------------------------------------------------------------ */
      /* Lifecycle                                                          */
      /* ------------------------------------------------------------------ */

      // Re-query everything on each open: product-info.js may have replaced
      // .product-media-modal__content innerHTML on variant change.
      refresh() {
        this.viewport = this.querySelector('[data-lightbox-viewport]');
        this.track = this.querySelector('[data-lightbox-track]');
        this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));
        this.counterCurrent = this.querySelector('[data-lightbox-current]');
        this.status = this.querySelector('[data-lightbox-status]');
        this.prevButton = this.querySelector('[data-lightbox-prev]');
        this.nextButton = this.querySelector('[data-lightbox-next]');
      }

      show(opener) {
        this.closing = false;
        this.classList.remove('lightbox-ui-hidden');
        // Move to the end of <body>: app scripts inject overlay nodes after
        // this element was parked here, and at equal z-index the later DOM
        // sibling paints on top. Must happen before focus is trapped.
        if (this.parentElement === document.body && document.body.lastElementChild !== this) {
          document.body.appendChild(this);
        }
        super.show(opener);
        this.refresh();
        if (!this.track || this.slides.length === 0) return;

        const mediaId = opener?.getAttribute('data-media-id');
        let startIndex = this.slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
        if (startIndex < 0) startIndex = 0;

        window.visualViewport?.addEventListener('resize', this.onViewportChange);
        window.addEventListener('resize', this.onViewportChange);

        this.resetZoom(false);
        this.goTo(startIndex, false);

        if (!this.reducedMotion.matches) {
          this.classList.add('lightbox-opening');
          requestAnimationFrame(() =>
            requestAnimationFrame(() => this.classList.remove('lightbox-opening'))
          );
        }
      }

      hide() {
        if (this.closing) return;
        this.closing = true;
        window.visualViewport?.removeEventListener('resize', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.pointers.clear();
        this.gesture = null;

        const finish = () => {
          this.classList.remove('lightbox-closing');
          this.style.removeProperty('--lightbox-fade');
          this.resetZoom(false);
          super.hide();
        };

        if (this.reducedMotion.matches || !this.hasAttribute('open')) {
          finish();
        } else {
          this.classList.add('lightbox-closing');
          setTimeout(finish, 220);
        }
      }

      onViewportChange() {
        // Address bar show/hide or rotation: re-measure and re-anchor without
        // animating, so the layout stays centered on Chrome Android.
        if (!this.hasAttribute('open') || !this.track) return;
        this.setTrack(-this.index * this.viewportWidth(), false);
        this.clampPan();
        this.applyZoom(false);
      }

      viewportWidth() {
        return this.viewport ? this.viewport.clientWidth : window.innerWidth;
      }

      /* ------------------------------------------------------------------ */
      /* Navigation                                                          */
      /* ------------------------------------------------------------------ */

      goTo(index, animate = true) {
        const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
        const changed = clamped !== this.index;
        this.index = clamped;
        this.resetZoom(false);
        this.setTrack(-clamped * this.viewportWidth(), animate && !this.reducedMotion.matches);
        this.syncUI();
        this.preloadNeighbors();
        if (changed) window.pauseAllMedia();
      }

      setTrack(x, animate) {
        if (!this.track) return;
        this.trackX = x;
        this.track.classList.toggle('is-animating', Boolean(animate));
        this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      }

      syncUI() {
        if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
        if (this.status) this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
        this.thumbs.forEach((thumb, i) => {
          const active = i === this.index;
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
          if (active) {
            thumb.scrollIntoView({
              block: 'nearest',
              inline: 'center',
              behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            });
          }
        });
        if (this.prevButton) this.prevButton.disabled = this.index === 0;
        if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
      }

      preloadNeighbors() {
        for (let i = this.index - 1; i <= this.index + 1; i++) {
          const img = this.slides[i]?.querySelector('img');
          if (img && img.loading === 'lazy') {
            img.loading = 'eager';
            if (!img.complete) img.decode?.().catch(() => {});
          }
        }
      }

      activeSlide() {
        return this.slides[this.index];
      }

      activeZoomer() {
        return this.activeSlide()?.querySelector('[data-lightbox-zoomer]');
      }

      activeImage() {
        const slide = this.activeSlide();
        return slide?.getAttribute('data-media-type') === 'image' ? slide.querySelector('img') : null;
      }

      /* ------------------------------------------------------------------ */
      /* Zoom / pan                                                          */
      /* ------------------------------------------------------------------ */

      resetZoom(animate) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.slides?.forEach((slide) => {
          const zoomer = slide.querySelector('[data-lightbox-zoomer]');
          if (zoomer) {
            zoomer.classList.toggle('is-animating', Boolean(animate));
            zoomer.style.transform = '';
          }
        });
      }

      // Ask the browser for a sharper srcset candidate the first time a slide
      // is actually zoomed (cheap high-res-on-demand, Amazon-style).
      upgradeResolution() {
        const img = this.activeImage();
        if (img && !img.dataset.zoomUpgraded) {
          img.dataset.zoomUpgraded = 'true';
          img.sizes = '200vw';
        }
      }

      clampPan(rubber = false) {
        const img = this.activeImage();
        if (!img) {
          this.tx = 0;
          this.ty = 0;
          return;
        }
        const maxX = Math.max(0, (img.offsetWidth * this.scale - this.viewportWidth()) / 2);
        const maxY = Math.max(0, (img.offsetHeight * this.scale - (this.viewport?.clientHeight || 0)) / 2);
        if (rubber) {
          this.tx = rubberClamp(this.tx, -maxX, maxX);
          this.ty = rubberClamp(this.ty, -maxY, maxY);
        } else {
          this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
          this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
        }
      }

      applyZoom(animate) {
        const zoomer = this.activeZoomer();
        if (!zoomer) return;
        zoomer.classList.toggle('is-animating', Boolean(animate && !this.reducedMotion.matches));
        zoomer.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      }

      scheduleZoomFrame() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.applyZoom(false);
        });
      }

      // Zoom keeping the image point under (px, py) stationary.
      // Coordinates are viewport-relative to the stage center.
      zoomTo(targetScale, px = 0, py = 0, animate = true) {
        if (!this.activeImage()) return;
        const newScale = Math.max(1, Math.min(MAX_ZOOM, targetScale));
        if (newScale > 1) this.upgradeResolution();
        const ratio = newScale / this.scale;
        this.tx = px - (px - this.tx) * ratio;
        this.ty = py - (py - this.ty) * ratio;
        this.scale = newScale;
        if (newScale === 1) {
          this.tx = 0;
          this.ty = 0;
        } else {
          this.clampPan();
        }
        this.applyZoom(animate);
      }

      stagePoint(clientX, clientY) {
        const rect = this.viewport.getBoundingClientRect();
        return {
          x: clientX - rect.left - rect.width / 2,
          y: clientY - rect.top - rect.height / 2,
        };
      }

      /* ------------------------------------------------------------------ */
      /* Pointer gestures                                                    */
      /* ------------------------------------------------------------------ */

      onPointerDown(event) {
        if (this.closing || !this.hasAttribute('open')) return;
        if (event.button !== 0) return;
        // Target guards apply only to the FIRST pointer (gesture initiation).
        // A second finger must always join the gesture: Chrome's fat-finger
        // hit-testing can snap a pinch finger onto a nearby button, and
        // rejecting it would silently degrade the pinch to a pan.
        if (this.pointers.size === 0) {
          // Let native widgets handle their own input.
          if (
            event.target.closest(
              'button, a, video, iframe, model-viewer, [data-lightbox-thumbs], .shopify-model-viewer-ui'
            )
          ) {
            return;
          }
          if (!event.target.closest('[data-lightbox-viewport]')) return;
        }

        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 1) {
          this.gesture = {
            type: null,
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            lastT: event.timeStamp,
            vx: 0,
            vy: 0,
            startTx: this.tx,
            startTy: this.ty,
            startTrackX: this.trackX,
            startTime: event.timeStamp,
            moved: false,
            target: event.target,
            pointerType: event.pointerType,
          };
        } else if (this.pointers.size === 2 && this.activeImage()) {
          const [p1, p2] = Array.from(this.pointers.values());
          const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
          this.gesture = {
            type: 'pinch',
            startDist: Math.hypot(p1.x - p2.x, p1.y - p2.y),
            startScale: this.scale,
            startTx: this.tx,
            startTy: this.ty,
            startMid: this.stagePoint(mid.x, mid.y),
            lastMid: mid,
            moved: true,
          };
          this.capturePointers();
          this.setTrack(-this.index * this.viewportWidth(), false);
        }
      }

      onPointerMove(event) {
        const pointer = this.pointers.get(event.pointerId);
        if (!pointer || !this.gesture) return;
        pointer.x = event.clientX;
        pointer.y = event.clientY;

        if (this.gesture.type === 'pinch') {
          this.handlePinch();
          return;
        }

        const g = this.gesture;
        const dx = event.clientX - g.startX;
        const dy = event.clientY - g.startY;

        if (!g.type) {
          if (Math.hypot(dx, dy) < DRAG_SLOP) return;
          if (this.scale > 1 && this.activeImage()) {
            g.type = 'pan';
          } else if (Math.abs(dy) > Math.abs(dx) * 1.2 && this.slidesAllowVerticalClose()) {
            g.type = 'vclose';
          } else {
            g.type = 'track';
          }
          g.moved = true;
          this.capturePointers();
        }

        // Velocity (light smoothing) for flick decisions.
        const dt = Math.max(1, event.timeStamp - g.lastT);
        g.vx = 0.8 * ((event.clientX - g.lastX) / dt) + 0.2 * g.vx;
        g.vy = 0.8 * ((event.clientY - g.lastY) / dt) + 0.2 * g.vy;
        g.lastX = event.clientX;
        g.lastY = event.clientY;
        g.lastT = event.timeStamp;

        if (g.type === 'pan') {
          this.tx = g.startTx + dx;
          this.ty = g.startTy + dy;
          this.clampPan(true);
          this.scheduleZoomFrame();
        } else if (g.type === 'track') {
          let x = g.startTrackX + dx;
          const min = -(this.slides.length - 1) * this.viewportWidth();
          x = rubberClamp(x, min, 0);
          this.setTrack(x, false);
        } else if (g.type === 'vclose') {
          const zoomer = this.activeZoomer();
          if (zoomer) {
            zoomer.classList.remove('is-animating');
            zoomer.style.transform = `translate3d(0, ${dy * 0.85}px, 0)`;
          }
          const fade = Math.max(0.35, 1 - Math.abs(dy) / 320);
          this.style.setProperty('--lightbox-fade', fade);
        }
      }

      onPointerUp(event) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        const g = this.gesture;
        if (!g) return;

        if (g.type === 'pinch') {
          if (this.pointers.size === 1) {
            // Settle sub-1x scale first: when both fingers lift in the same
            // frame the second pointerup lands in the pan branch and would
            // otherwise never snap the scale back (or close on deep pinch-in).
            if (this.scale < 0.8) {
              this.gesture = null;
              this.pointers.clear();
              this.hide();
              return;
            }
            if (this.scale < 1) this.zoomTo(1, 0, 0, true);
            // Hand off pinch → single-finger pan without a jump.
            const remaining = Array.from(this.pointers.values())[0];
            this.gesture = {
              type: 'pan',
              startX: remaining.x,
              startY: remaining.y,
              lastX: remaining.x,
              lastY: remaining.y,
              lastT: event.timeStamp,
              vx: 0,
              vy: 0,
              startTx: this.tx,
              startTy: this.ty,
              startTrackX: this.trackX,
              moved: true,
            };
          } else if (this.pointers.size === 0) {
            this.settlePinch();
            this.gesture = null;
          }
          return;
        }

        if (this.pointers.size > 0) return;
        this.gesture = null;

        if (!g.moved) {
          this.handleTap(event, g);
          return;
        }

        if (g.type === 'pan') {
          // Small momentum step, then clamp back into bounds.
          this.tx += g.vx * 120;
          this.ty += g.vy * 120;
          this.clampPan();
          this.applyZoom(true);
        } else if (g.type === 'track') {
          const dx = g.lastX - g.startX;
          const width = this.viewportWidth();
          let target = this.index;
          if (Math.abs(dx) > width * SWIPE_DISTANCE_RATIO || Math.abs(g.vx) > SWIPE_VELOCITY) {
            target = this.index + (dx < 0 ? 1 : -1);
          }
          this.goTo(target, true);
        } else if (g.type === 'vclose') {
          const dy = g.lastY - g.startY;
          if (Math.abs(dy) > CLOSE_DRAG_DISTANCE || Math.abs(g.vy) > CLOSE_DRAG_VELOCITY) {
            this.hide();
          } else {
            this.style.removeProperty('--lightbox-fade');
            const zoomer = this.activeZoomer();
            if (zoomer) {
              zoomer.classList.toggle('is-animating', !this.reducedMotion.matches);
              zoomer.style.transform = '';
            }
          }
        }
      }

      capturePointers() {
        // Capture on the viewport child, NOT on `this`: the browser retargets
        // the post-drag synthesized click to the capture target, and
        // ModalDialog's baseline `click on self → hide()` listener would close
        // the viewer after every swipe/pan if `this` were the target.
        const captureTarget = this.viewport || this;
        this.pointers.forEach((_, id) => {
          try {
            captureTarget.setPointerCapture(id);
          } catch (error) {
            // Pointer may already be gone (e.g. cancelled) — safe to ignore.
          }
        });
      }

      handlePinch() {
        const g = this.gesture;
        const [p1, p2] = Array.from(this.pointers.values());
        if (!p1 || !p2) return;
        const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        let scale = g.startScale * (dist / g.startDist);
        scale = rubberClamp(scale, 1, MAX_ZOOM, 0.35);
        if (scale > 1) this.upgradeResolution();

        const point = this.stagePoint(mid.x, mid.y);
        const ratio = scale / g.startScale;
        this.tx = point.x - (g.startMid.x - g.startTx) * ratio;
        this.ty = point.y - (g.startMid.y - g.startTy) * ratio;
        this.scale = scale;
        this.clampPan(true);
        this.scheduleZoomFrame();
      }

      settlePinch() {
        if (this.scale < 0.8) {
          // Pinch well below 1x closes the viewer (PhotoSwipe convention).
          this.hide();
          return;
        }
        if (this.scale <= 1) {
          this.zoomTo(1, 0, 0, true);
        } else {
          this.scale = Math.min(MAX_ZOOM, this.scale);
          this.clampPan();
          this.applyZoom(true);
        }
      }

      slidesAllowVerticalClose() {
        // Vertical drag-to-close only from image slides at 1x.
        return this.scale === 1 && Boolean(this.activeImage());
      }

      /* ------------------------------------------------------------------ */
      /* Taps, clicks, wheel, keys                                           */
      /* ------------------------------------------------------------------ */

      handleTap(event, gesture) {
        const point = this.stagePoint(event.clientX, event.clientY);
        // The zoomer wrapper fills the whole slide, so hit-test the rendered
        // image rect: taps beside the image are "backdrop" taps.
        const img = this.activeImage();
        const rect = img?.getBoundingClientRect();
        const onImage = Boolean(
          rect &&
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        );

        if (gesture.pointerType === 'mouse') {
          if (onImage && this.activeImage()) {
            this.zoomTo(this.scale > 1 ? 1 : DOUBLE_TAP_ZOOM, point.x, point.y, true);
          } else {
            this.hide();
          }
          return;
        }

        const now = event.timeStamp;
        if (
          this.lastTap &&
          now - this.lastTap.t < DOUBLE_TAP_MS &&
          Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < DOUBLE_TAP_RADIUS
        ) {
          clearTimeout(this.singleTapTimer);
          this.lastTap = null;
          if (this.activeImage()) {
            this.zoomTo(this.scale > 1 ? 1 : DOUBLE_TAP_ZOOM, point.x, point.y, true);
          }
          return;
        }

        this.lastTap = { t: now, x: event.clientX, y: event.clientY };
        clearTimeout(this.singleTapTimer);
        this.singleTapTimer = setTimeout(() => {
          this.lastTap = null;
          if (onImage) {
            this.classList.toggle('lightbox-ui-hidden');
          } else {
            this.hide();
          }
        }, DOUBLE_TAP_MS);
      }

      onClick(event) {
        const thumb = event.target.closest('[data-lightbox-thumb]');
        if (thumb) {
          this.goTo(this.thumbs.indexOf(thumb), true);
          return;
        }
        if (event.target.closest('[data-lightbox-prev]')) this.goTo(this.index - 1, true);
        if (event.target.closest('[data-lightbox-next]')) this.goTo(this.index + 1, true);
      }

      onWheel(event) {
        if (!this.hasAttribute('open') || !this.activeImage()) return;
        if (!event.target.closest('[data-lightbox-viewport]')) return;
        event.preventDefault();
        const point = this.stagePoint(event.clientX, event.clientY);
        const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.002));
        let target = this.scale * factor;
        if (target < 1.05) target = 1;
        this.zoomTo(target, point.x, point.y, false);
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            this.goTo(this.index - 1, true);
            break;
          case 'ArrowRight':
            event.preventDefault();
            this.goTo(this.index + 1, true);
            break;
          case '+':
          case '=':
            event.preventDefault();
            this.zoomTo(this.scale + 0.75, 0, 0, true);
            break;
          case '-':
            event.preventDefault();
            this.zoomTo(this.scale - 0.75, 0, 0, true);
            break;
          case '0':
            event.preventDefault();
            this.zoomTo(1, 0, 0, true);
            break;
        }
      }
    }
  );

  // Soft clamp: linear inside [min, max], resistance beyond the edges.
  function rubberClamp(value, min, max, resistance = 0.3) {
    if (value < min) return min + (value - min) * resistance;
    if (value > max) return max + (value - max) * resistance;
    return value;
  }
}
